import { useState, useEffect, useCallback } from "react";
import { useParams, useNavigate, useSearchParams } from "react-router-dom";
import { Layout } from "../components/Layout";
import { AgentChatSheet } from "../components/ai/AgentChatSheet";
import { useAuth } from "../contexts/AuthContext";
import { useLanguage } from "../contexts/LanguageContext";
import {
	athleteSportsOf,
	fetchAPI,
	formatDate,
	formatFullName,
	formatRestDisplay,
	formatSportLabel,
} from "../lib/utils";
import { getImageUrl } from "../lib/coachUtils";
import { getProgramDayCount } from "../lib/programVisibility";
import {
	hasContent,
	formatIntervalSecondsLabel,
	getEmomRoundLabel,
	formatEmomItemSummary,
	resolveConditioningResultPresentation,
} from "../lib/conditioningResultUtils";
import { toast } from "sonner";
import {
	Activity,
	Bot,
	ArrowLeft,
	Calendar,
	ChevronLeft,
	ChevronRight,
	ClipboardCheck,
	LineChart,
	Loader2,
	User,
} from "lucide-react";
import { Button } from "../components/ui/button";
import { Card, CardContent } from "../components/ui/card";
import { Badge } from "../components/ui/badge";
import { CoachAthletePerformanceProfile } from "../components/CoachAthletePerformanceProfile";
import { ShootingProfileSection } from "../components/shooting/ShootingProfileSection";
import { DOMAIN_SHOOTING, hasDomain } from "../lib/domainAccess";
import { AthletePlanningCalendar } from "../components/planning/AthletePlanningCalendar";
import {
	Select,
	SelectContent,
	SelectItem,
	SelectTrigger,
	SelectValue,
} from "../components/ui/select";

function getStartOfWeek(date) {
	const value = new Date(date);
	value.setHours(0, 0, 0, 0);
	const day = value.getDay();
	const diff = day === 0 ? -6 : 1 - day;
	value.setDate(value.getDate() + diff);
	return value;
}

function addDays(date, days) {
	const value = new Date(date);
	value.setDate(value.getDate() + days);
	return value;
}

function toDateKey(date) {
	return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, "0")}-${String(
		date.getDate(),
	).padStart(2, "0")}`;
}

export default function AthleteDetail() {
	const { athleteId } = useParams();
	// Analisi dei risultati dell'atleta: prima si poteva chiedere solo dal
	// dock del Program Builder, che per un'analisi e' il posto sbagliato.
	const [isAiOpen, setIsAiOpen] = useState(false);
	const navigate = useNavigate();
	const { user } = useAuth();
	const { t, language } = useLanguage();

	// Athlete data
	const [athlete, setAthlete] = useState(null);
	const [loading, setLoading] = useState(true);

	// Active section. `?tab=` lets other screens (the builder's agenda dock)
	// land on a specific section instead of relying on the default.
	const [searchParams] = useSearchParams();
	const [activeSection, setActiveSection] = useState(() => {
		const requested = searchParams.get("tab");
		return ["planning", "results", "info", "profile"].includes(requested)
			? requested
			: "planning";
	});

	// Agenda data (the planning calendar itself lives in
	// components/planning/AthletePlanningCalendar.js)
	const [competitions, setCompetitions] = useState([]);
	const [loadingCompetitions, setLoadingCompetitions] = useState(false);
	const [mesocycles, setMesocycles] = useState([]);
	const [loadingMesocycles, setLoadingMesocycles] = useState(false);

	// Results
	const [results, setResults] = useState([]);
	const [loadingResults, setLoadingResults] = useState(false);
	const [resultsWeekStart, setResultsWeekStart] = useState(() =>
		getStartOfWeek(new Date()),
	);
	const [weeksToShow, setWeeksToShow] = useState(2);

	// Etichette delle discipline (dato admin, non si traducono): servono
	// all'intestazione e ai tab per sport del profilo prestativo.
	const [sportsOptions, setSportsOptions] = useState([]);

	// Programs
	const [programs, setPrograms] = useState([]);

	// ── Load athlete data ──
	const loadAthlete = useCallback(async () => {
		try {
			const data = await fetchAPI(`/users/${athleteId}`);
			setAthlete(data);
		} catch {
			toast.error(t("athleteNotFound"));
			navigate("/athletes");
		}
	}, [athleteId, navigate, t]);

	const loadCompetitions = useCallback(async () => {
		setLoadingCompetitions(true);
		try {
			const data = await fetchAPI(`/coach/athletes/${athleteId}/competitions`);
			setCompetitions(data || []);
		} catch {
			setCompetitions([]);
		} finally {
			setLoadingCompetitions(false);
		}
	}, [athleteId]);

	const loadMesocycles = useCallback(async () => {
		setLoadingMesocycles(true);
		try {
			const data = await fetchAPI(`/mesocycles/athlete/${athleteId}`);
			setMesocycles(data || []);
		} catch {
			setMesocycles([]);
		} finally {
			setLoadingMesocycles(false);
		}
	}, [athleteId]);

	const loadResults = useCallback(async () => {
		setLoadingResults(true);
		try {
			const data = await fetchAPI(
				`/coach/athletes/${athleteId}/agenda-results`,
			);
			setResults(data || []);
		} catch {
			setResults([]);
		} finally {
			setLoadingResults(false);
		}
	}, [athleteId]);

	useEffect(() => {
		let cancelled = false;
		fetchAPI("/users/sports/list")
			.then((data) => {
				if (!cancelled) setSportsOptions(Array.isArray(data) ? data : []);
			})
			.catch(() => {});
		return () => {
			cancelled = true;
		};
	}, []);

	const loadPrograms = useCallback(async () => {
		try {
			const data = await fetchAPI("/programs?scope=mine&compact=true");
			const athletePrograms = (data || []).filter((p) =>
				(p.assigned_athletes || []).some((a) => a.athlete_id === athleteId),
			);
			setPrograms(athletePrograms);
		} catch {
			setPrograms([]);
		}
	}, [athleteId]);

	useEffect(() => {
		if (!user || (user.role !== "coach" && user.role !== "admin")) {
			navigate("/dashboard");
			return;
		}
		setLoading(true);
		Promise.all([loadAthlete(), loadCompetitions(), loadMesocycles()]).finally(
			() => setLoading(false),
		);
	}, [user, navigate, loadAthlete, loadCompetitions, loadMesocycles]);

	useEffect(() => {
		if (
			activeSection === "results" &&
			results.length === 0 &&
			!loadingResults
		) {
			loadResults();
		}
		if (activeSection === "info" && programs.length === 0) {
			loadPrograms();
		}
	}, [
		activeSection,
		results.length,
		loadingResults,
		loadResults,
		programs.length,
		loadPrograms,
	]);

	const visibleResultWeeks = Array.from({ length: weeksToShow }, (_, index) => {
		const weekStart = addDays(resultsWeekStart, index * 7);
		const days = Array.from({ length: 7 }, (_, dayIndex) => {
			const date = addDays(weekStart, dayIndex);
			return {
				date,
				dateKey: toDateKey(date),
				label: date.toLocaleDateString(language === "it" ? "it-IT" : "en-US", {
					weekday: "short",
					day: "numeric",
					month: "short",
				}),
			};
		});
		return {
			weekStart,
			weekEnd: addDays(weekStart, 6),
			days,
		};
	});

	const resultsByDate = results.reduce((accumulator, result) => {
		const key = result?.date;
		if (!key) return accumulator;
		if (!accumulator[key]) accumulator[key] = [];
		accumulator[key].push(result);
		return accumulator;
	}, {});

	const hasLoggedResultContent = (result) => {
		if (!result) return false;
		if (result.weight_kg !== null && result.weight_kg !== undefined)
			return true;
		if (
			Array.isArray(result.interval_scores) &&
			result.interval_scores.length > 0
		)
			return true;
		if (result.reps !== null && result.reps !== undefined) return true;
		if (hasContent(result.reps_text)) return true;
		if (result.time_seconds !== null && result.time_seconds !== undefined)
			return true;
		if (hasContent(result.score)) return true;
		if (result.rpe !== null && result.rpe !== undefined) return true;
		if (hasContent(result.notes)) return true;
		if (
			Array.isArray(result.superset_details) &&
			result.superset_details.some(
				(detail) =>
					detail?.weight_kg ||
					detail?.reps ||
					detail?.rpe ||
					hasContent(detail?.notes),
			)
		)
			return true;
		if (
			Array.isArray(result.result_set_rows) &&
			result.result_set_rows.some(
				(row) =>
					row?.weight_kg || row?.reps || row?.rpe || hasContent(row?.notes),
			)
		)
			return true;
		return false;
	};

	const hasMeaningfulDisplayValue = (value) => {
		if (value === null || value === undefined) return false;
		if (typeof value === "number") return value > 0;
		const text = String(value).trim();
		if (!text) return false;
		if (text === "0" || text === "0.0" || text === "0.00") return false;
		return true;
	};

	const formatProfileMetricValue = (value, unit) => {
		if (!hasMeaningfulDisplayValue(value)) return "-";
		const numericValue = Number(value);
		if (!Number.isFinite(numericValue)) return String(value);

		switch ((unit || "").toLowerCase()) {
			case "seconds": {
				const totalSeconds = Math.max(0, Math.round(numericValue));
				const minutes = Math.floor(totalSeconds / 60);
				const seconds = totalSeconds % 60;
				return minutes > 0
					? `${minutes}:${String(seconds).padStart(2, "0")}`
					: `${seconds}s`;
			}
			case "minutes_seconds": {
				const totalSeconds = Math.max(0, Math.round(numericValue));
				const minutes = Math.floor(totalSeconds / 60);
				const seconds = totalSeconds % 60;
				return `${minutes}:${String(seconds).padStart(2, "0")}`;
			}
			case "kg":
				return `${numericValue} kg`;
			// Rapporto punti/tempo: i decimali distinguono due prestazioni che
			// arrotondate sarebbero lo stesso numero.
			case "hit_factor":
				return `HF ${numericValue.toFixed(2)}`;
			default:
				return String(numericValue);
		}
	};

	const shouldShowMethodology = (value) => {
		if (!hasMeaningfulDisplayValue(value)) return false;
		const normalized = String(value).trim().toLowerCase();
		return normalized !== "standard" && normalized !== "default";
	};

	Object.values(resultsByDate).forEach((items) => {
		items.sort((left, right) => {
			const leftName = String(left?.exercise_name || "");
			const rightName = String(right?.exercise_name || "");
			return leftName.localeCompare(rightName);
		});
	});

	const agendaWeeks = visibleResultWeeks.map((weekItem) => {
		const activeDays = weekItem.days
			.map((dayItem) => {
				const dayResults = resultsByDate[dayItem.dateKey] || [];
				const hasEntries = dayResults.length > 0;
				const hasLoggedResults = dayResults.some((result) =>
					hasLoggedResultContent(result),
				);
				return {
					...dayItem,
					dayResults,
					hasEntries,
					hasLoggedResults,
				};
			})
			.filter((dayItem) => dayItem.hasEntries);

		return {
			...weekItem,
			activeDays,
		};
	});

	const resultsRangeLabel = (() => {
		const firstDay = visibleResultWeeks[0]?.weekStart;
		const lastDay = visibleResultWeeks[visibleResultWeeks.length - 1]?.weekEnd;
		if (!firstDay || !lastDay) return "";
		const locale = language === "it" ? "it-IT" : "en-US";
		return `${firstDay.toLocaleDateString(locale, {
			day: "numeric",
			month: "short",
			year: "numeric",
		})} - ${lastDay.toLocaleDateString(locale, {
			day: "numeric",
			month: "short",
			year: "numeric",
		})}`;
	})();

	const shiftResultsWeeks = (direction) => {
		setResultsWeekStart((prev) => addDays(prev, direction * weeksToShow * 7));
	};

	const renderAgendaResultCard = (result) => {
		const prescription =
			result?.prescription?.exercise || result?.conditioning_config || null;
		const conditioningView = resolveConditioningResultPresentation({
			result,
			prescriptionExercise: prescription,
			programExercise: null,
			language,
		});
		const conditioningType = conditioningView.condType;
		const isConditioningPrescription = conditioningView.isConditioning;
		const conditioningExercises = conditioningView.condExercises;
		const emomRounds = conditioningView.emomRounds;
		const displayScore = conditioningView.displayScore;
		const displayNotes = conditioningView.displayNotes;
		const prescriptionRows = Array.isArray(prescription?.set_rows)
			? prescription.set_rows
			: [];
		const resultRows = Array.isArray(result?.result_set_rows)
			? result.result_set_rows
			: [];
		const methodologyValue = prescription?.methodology;
		const hasRows = resultRows.some(
			(row) => row?.weight_kg || row?.reps || row?.rpe,
		);

		return (
			<div
				key={result.result_id}
				className="border border-border rounded-sm p-3 space-y-2 bg-background/70"
			>
				<div className="flex items-center justify-between gap-2">
					<p className="font-heading text-sm truncate">
						{result.exercise_name || "-"}
					</p>
					{result.exercise_type && (
						<Badge className="rounded-sm bg-volt/20 text-volt border-0 text-[10px] capitalize">
							{result.exercise_type}
						</Badge>
					)}
				</div>

				{(result?.prescription?.program_name ||
					result?.prescription?.day_name) && (
					<div className="text-[11px] text-muted-foreground flex flex-wrap gap-x-3 gap-y-1">
						{result?.prescription?.program_name && (
							<span>
								{t("programLabel")}: {result.prescription.program_name}
							</span>
						)}
						{result?.prescription?.day_name && (
							<span>{result.prescription.day_name}</span>
						)}
					</div>
				)}

				{prescription && (
					<div className="bg-secondary/40 border border-border/70 rounded-sm p-2">
						<p className="text-[10px] uppercase tracking-wider text-muted-foreground mb-1">
							{t("prescriptionLabel")}
						</p>
						<div className="text-xs text-muted-foreground flex flex-wrap gap-x-3 gap-y-1">
							{!isConditioningPrescription &&
								shouldShowMethodology(methodologyValue) && (
									<span>
										{t("methodology")}: {methodologyValue}
									</span>
								)}
							{isConditioningPrescription &&
								shouldShowMethodology(conditioningType) && (
									<span>
										{t("methodology")}: {String(conditioningType).toUpperCase()}
									</span>
								)}
							{!isConditioningPrescription &&
								hasMeaningfulDisplayValue(prescription.sets) && (
									<span>
										{t("setsShort")}: {prescription.sets}
									</span>
								)}
							{!isConditioningPrescription &&
								hasMeaningfulDisplayValue(prescription.reps) && (
									<span>
										{t("repsLabel")}: {prescription.reps}
									</span>
								)}
							{!isConditioningPrescription &&
								hasMeaningfulDisplayValue(prescription.load_percentage) && (
									<span>{prescription.load_percentage}%</span>
								)}
							{!isConditioningPrescription &&
								hasMeaningfulDisplayValue(prescription.load_kg) && (
									<span>{prescription.load_kg} kg</span>
								)}
							{hasMeaningfulDisplayValue(prescription.time_seconds) && (
								<span>
									{t("timeLabel")}:{" "}
									{formatProfileMetricValue(
										prescription.time_seconds,
										"seconds",
									)}
								</span>
							)}
							{hasMeaningfulDisplayValue(prescription.rest_seconds) && (
								<span>
									{t("restLabel")}:{" "}
									{formatProfileMetricValue(
										prescription.rest_seconds,
										"seconds",
									)}
								</span>
							)}
							{hasMeaningfulDisplayValue(prescription.rpe) && (
								<span>RPE: {prescription.rpe}</span>
							)}
							{hasMeaningfulDisplayValue(prescription.rir) && (
								<span>RIR: {prescription.rir}</span>
							)}
							{isConditioningPrescription &&
								hasMeaningfulDisplayValue(prescription.time_cap_seconds) && (
									<span>
										TC{" "}
										{formatProfileMetricValue(
											prescription.time_cap_seconds,
											"seconds",
										)}
									</span>
								)}
						</div>
						{prescriptionRows.length > 0 && (
							<div className="space-y-1 mt-1">
								{prescriptionRows.map((row, idx) => (
									<div
										key={idx}
										className="text-xs text-muted-foreground border-l-2 border-border pl-2 flex flex-wrap gap-x-3 gap-y-1"
									>
										<span className="text-[10px] uppercase tracking-wider">
											{t("setsShort")} {idx + 1}
										</span>
										{hasMeaningfulDisplayValue(row?.reps) && (
											<span>
												{t("repsLabel")}: {row.reps}
											</span>
										)}
										{hasMeaningfulDisplayValue(row?.load_percentage) && (
											<span>{row.load_percentage}%</span>
										)}
										{hasMeaningfulDisplayValue(row?.load_kg) && (
											<span>{row.load_kg} kg</span>
										)}
									</div>
								))}
							</div>
						)}
						{Array.isArray(prescription.superset_exercises) &&
							prescription.superset_exercises.length > 0 && (
								<div className="text-xs text-muted-foreground flex flex-wrap gap-x-2 gap-y-1 mt-1">
									{prescription.superset_exercises.map((item, idx) => (
										<span key={idx}>
											{idx > 0 && " + "}
											{item?.exercise_name}
											{item?.reps ? ` (${item.reps})` : ""}
										</span>
									))}
								</div>
							)}
						{conditioningType === "emom" && emomRounds.length > 0 && (
							<div className="pl-0 space-y-1.5 mt-1">
								{emomRounds.map((round, j) => (
									<div
										key={round.round_id || j}
										className="border border-yellow-500/20 bg-yellow-500/10 rounded-sm px-2 py-1.5"
									>
										<div className="text-[10px] uppercase tracking-wider text-yellow-400 font-medium mb-1">
											{getEmomRoundLabel(round, j, language)}
										</div>
										<div className="space-y-0.5">
											{round.items.map((item, itemIndex) => (
												<span
													key={item.item_id || `${j}-${itemIndex}`}
													className="block text-xs text-muted-foreground"
												>
													{formatEmomItemSummary(item, language)}
												</span>
											))}
										</div>
									</div>
								))}
							</div>
						)}
						{(conditioningType !== "emom" || emomRounds.length === 0) &&
							conditioningExercises.length > 0 && (
								<div className="text-xs text-muted-foreground space-y-1 mt-1">
									{conditioningExercises.map((item, idx) => (
										<div key={idx}>
											{item?.reps ? `${item.reps} ` : ""}
											{item?.exercise_name || item?.name}
										</div>
									))}
								</div>
							)}
						{conditioningType === "emom" && (
							<div className="text-xs space-y-1 mt-2 pt-2 border-t border-border/60">
								{conditioningView.intervalLabel && (
									<div className="text-volt">
										{t("everyLabel")} {conditioningView.intervalLabel}
									</div>
								)}
								{conditioningView.rounds !== null &&
									conditioningView.rounds !== undefined &&
									String(conditioningView.rounds).trim() !== "" &&
									String(conditioningView.rounds).trim() !== "0" && (
										<div className="text-volt">
											{conditioningView.rounds}{" "}
											{conditioningView.rounds === 1 ? "round" : "rounds"}
										</div>
									)}
							</div>
						)}
						{prescription.notes && (
							<p className="text-xs text-muted-foreground whitespace-pre-wrap border-t border-border/60 pt-1 mt-1">
								{t("coachNotes")}: {prescription.notes}
							</p>
						)}
					</div>
				)}

				<div className="bg-green-500/10 border border-green-500/20 rounded-sm p-2">
					<p className="text-[10px] uppercase tracking-wider text-green-400 mb-1">
						{t("resultLabel")}
					</p>
					{result?.exercise_type === "conditioning" && (
						<div className="flex items-center gap-1.5 mb-2 flex-wrap">
							{result?.scaling ? (
								<>
									<span className="inline-flex items-center rounded-sm bg-amber-500/15 text-amber-300 border border-amber-500/30 px-1.5 py-0.5 text-[10px] font-heading uppercase tracking-wider">
										{t("scalingScaledLabel") || "SCALED"}
									</span>
									<span className="text-[11px] text-muted-foreground break-words">
										{result.scaling}
									</span>
								</>
							) : (
								<span className="inline-flex items-center rounded-sm bg-green-500/15 text-green-300 border border-green-500/30 px-1.5 py-0.5 text-[10px] font-heading uppercase tracking-wider">
									{t("scalingRxLabel") || "RX"}
								</span>
							)}
						</div>
					)}
					{Array.isArray(result.superset_details) &&
						result.superset_details.length > 0 && (
							<div className="space-y-1 mb-1">
								{result.superset_details.map((detail, idx) => (
									<div
										key={idx}
										className="text-xs text-muted-foreground border-l-2 border-green-500/30 pl-2 flex flex-wrap gap-x-3 gap-y-1"
									>
										<span className="text-[10px] uppercase tracking-wider text-green-400">
											{String.fromCharCode(65 + idx)}
										</span>
										<span className="font-medium text-foreground">
											{detail.exercise_name}
										</span>
										{detail.weight_kg && <span>{detail.weight_kg} kg</span>}
										{detail.reps && (
											<span>
												{t("repsLabel")}: {detail.reps}
											</span>
										)}
										{detail.rpe && <span>RPE {detail.rpe}</span>}
									</div>
								))}
							</div>
						)}
					{hasRows && (
						<div className="space-y-1 mb-1">
							{resultRows.map((row, idx) => {
								const hasRow = row?.weight_kg || row?.reps || row?.rpe;
								if (!hasRow) return null;
								return (
									<div
										key={idx}
										className="text-xs text-muted-foreground border-l-2 border-green-500/30 pl-2 flex flex-wrap gap-x-3 gap-y-1"
									>
										<span className="text-[10px] uppercase tracking-wider text-green-400">
											{t("setsShort")} {idx + 1}
										</span>
										{row.weight_kg && <span>{row.weight_kg} kg</span>}
										{row.reps && (
											<span>
												{t("repsLabel")}: {row.reps}
											</span>
										)}
										{row.rpe && <span>RPE {row.rpe}</span>}
									</div>
								);
							})}
						</div>
					)}
					{hasLoggedResultContent(result) ? (
						<div className="text-xs text-muted-foreground flex flex-wrap gap-x-3 gap-y-1">
							{result.weight_kg !== null && result.weight_kg !== undefined && (
								<span>
									{t("weightKgLabel")}: {result.weight_kg}
								</span>
							)}
							{Array.isArray(result.interval_scores) &&
								result.interval_scores.length > 0 && (
									<span>Ripetute: {result.interval_scores.join(" · ")}</span>
								)}
							{(result.reps_text || result.reps) && (
								<span>
									{t("repsLabel")}: {result.reps_text || result.reps}
								</span>
							)}
							{result.time_seconds !== null &&
								result.time_seconds !== undefined && (
									<span>
										{t("timeLabel")}:{" "}
										{formatProfileMetricValue(result.time_seconds, "seconds")}
									</span>
								)}
							{hasContent(displayScore) && (
								<span>
									{t("scoreLabel")}: {displayScore}
								</span>
							)}
							{result.rpe !== null && result.rpe !== undefined && (
								<span>RPE: {result.rpe}</span>
							)}
						</div>
					) : (
						<div className="inline-flex items-center rounded-sm border border-dashed border-green-500/30 px-2 py-1 text-[10px] uppercase tracking-wider text-green-300">
							{t("noResultsLoadedShort")}
						</div>
					)}
				</div>

				<div className="bg-volt/5 border border-volt/20 rounded-sm p-2">
					<p className="text-[10px] uppercase tracking-wider text-volt mb-1">
						{t("workoutFeedback")}
					</p>
					{displayNotes ? (
						<p className="text-xs text-muted-foreground whitespace-pre-wrap">
							{displayNotes}
						</p>
					) : (
						<p className="text-xs text-muted-foreground">
							{t("noFeedbackLabel")}
						</p>
					)}
				</div>
			</div>
		);
	};

	// ── Sections ──
	const sections = [
		{ key: "planning", label: t("mesocyclePlanning"), icon: Calendar },
		{ key: "results", label: t("resultsAnalysis"), icon: ClipboardCheck },
		{ key: "info", label: t("athleteInfo"), icon: User },
		{ key: "profile", label: t("performanceProfile"), icon: LineChart },
	];

	if (loading || !athlete) {
		return (
			<Layout>
				<div className="flex items-center justify-center h-[50vh] text-muted-foreground">
					<Loader2 className="w-5 h-5 animate-spin mr-2" />
					{t("loading")}
				</div>
			</Layout>
		);
	}

	const athletePictureUrl = getImageUrl(athlete.picture || "");

	return (
		<Layout>
			<div className="max-w-7xl mx-auto px-4 md:px-6 py-6 md:py-8 pb-24 md:pb-8 space-y-6">
				{/* ── Header ── */}
				<div className="flex items-center gap-4">
					<Button
						variant="ghost"
						size="icon"
						className="rounded-sm text-muted-foreground hover:text-volt"
						onClick={() => navigate("/athletes")}
					>
						<ArrowLeft className="w-5 h-5" />
					</Button>
					<div className="flex items-center gap-3 flex-1 min-w-0">
						{athletePictureUrl ? (
							<img
								src={athletePictureUrl}
								alt=""
								className="w-10 h-10 rounded-full object-cover border border-border"
								onError={(e) => {
									e.target.style.display = "none";
								}}
							/>
						) : (
							<div className="w-10 h-10 rounded-full bg-volt/20 text-volt flex items-center justify-center text-sm font-heading">
								{(athlete.name || "?").charAt(0).toUpperCase()}
							</div>
						)}
						<div className="min-w-0">
							<h1 className="font-heading text-xl tracking-tight truncate">
								{athlete.nickname || formatFullName(athlete) || athlete.name}
							</h1>
							<p className="text-xs text-muted-foreground truncate">
								{athlete.email}
								{athleteSportsOf(athlete).length > 0 &&
									` · ${athleteSportsOf(athlete)
										.map((sport) => formatSportLabel(sport, sportsOptions))
										.join(" · ")}`}
							</p>
						</div>
						{/* Il monitoraggio dello stesso atleta: le due schede si collegano in entrambi i versi (D32). */}
						<Button
							variant="outline"
							size="sm"
							onClick={() => navigate(`/monitoring/${athleteId}`)}
							className="ml-auto shrink-0 rounded-sm"
							aria-label={t("monitoring")}
							data-testid="athlete-monitoring-btn"
						>
							<Activity className="w-4 h-4 sm:mr-2" aria-hidden="true" />
							<span className="hidden sm:inline">{t("monitoring")}</span>
						</Button>
						<Button
							variant="outline"
							size="sm"
							onClick={() => setIsAiOpen(true)}
							className="shrink-0 rounded-sm border-volt/40 text-volt hover:bg-volt/10"
							data-testid="athlete-ai-btn"
						>
							<Bot className="w-4 h-4 sm:mr-2" />
							<span className="hidden sm:inline">Chiedi all'AI</span>
						</Button>
					</div>
				</div>

				{/* ── Section Nav ── */}
				<div className="grid grid-cols-4 gap-1 border-b border-border pb-1 sm:flex sm:gap-1 sm:pb-0">
					{sections.map((sec) => {
						const Icon = sec.icon;
						return (
							<button
								key={sec.key}
								onClick={() => setActiveSection(sec.key)}
								className={`flex flex-col sm:flex-row items-center justify-center gap-1 px-1.5 sm:px-3 py-2 text-[10px] sm:text-sm font-medium border-b-2 transition-colors min-w-0 ${
									activeSection === sec.key
										? "border-volt text-volt"
										: "border-transparent text-muted-foreground hover:text-foreground"
								}`}
							>
								<Icon className="w-4 h-4" />
								<span className="truncate max-w-full">{sec.label}</span>
							</button>
						);
					})}
				</div>

				{/* ════════ PLANNING SECTION ════════ */}
				{activeSection === "planning" && (
					<AthletePlanningCalendar
						athleteId={athleteId}
						mesocycles={mesocycles}
						competitions={competitions}
						loading={loadingCompetitions || loadingMesocycles}
						onMesocyclesChanged={loadMesocycles}
						onCompetitionsChanged={loadCompetitions}
					/>
				)}

				{/* ════════ RESULTS SECTION ════════ */}
				{activeSection === "results" && (
					<div className="space-y-4">
						<div className="flex flex-wrap items-center justify-between gap-3">
							<p className="label-tactical">{t("recentResults")}</p>
							<div className="flex items-center gap-2">
								<span className="text-[10px] text-muted-foreground uppercase tracking-widest">
									{t("weeksVisible")}
								</span>
								<Select
									value={String(weeksToShow)}
									onValueChange={(value) => setWeeksToShow(Number(value))}
								>
									<SelectTrigger className="w-[92px] h-8 rounded-sm bg-background border-border text-xs">
										<SelectValue />
									</SelectTrigger>
									<SelectContent className="bg-card border-border rounded-sm">
										<SelectItem value="1" className="text-xs">
											1 {t("weekSingular")}
										</SelectItem>
										<SelectItem value="2" className="text-xs">
											2 {t("weeksPlural")}
										</SelectItem>
										<SelectItem value="3" className="text-xs">
											3 {t("weeksPlural")}
										</SelectItem>
									</SelectContent>
								</Select>
							</div>
						</div>
						{loadingResults ? (
							<div className="flex items-center justify-center py-10 text-muted-foreground">
								<Loader2 className="w-5 h-5 animate-spin mr-2" />
								{t("loading")}
							</div>
						) : results.length === 0 ? (
							<Card className="bg-card border-border rounded-sm">
								<CardContent className="py-10 text-center">
									<ClipboardCheck className="w-8 h-8 text-muted-foreground mx-auto mb-2" />
									<p className="text-sm text-muted-foreground">
										{t("noAgendaResults")}
									</p>
								</CardContent>
							</Card>
						) : (
							<div className="space-y-4">
								<div className="flex flex-wrap items-center justify-between gap-3">
									<div className="flex items-center gap-2">
										<Button
											variant="ghost"
											size="icon"
											className="rounded-sm h-8 w-8"
											onClick={() => shiftResultsWeeks(-1)}
										>
											<ChevronLeft className="w-4 h-4" />
										</Button>
										<Button
											variant="ghost"
											size="icon"
											className="rounded-sm h-8 w-8"
											onClick={() => shiftResultsWeeks(1)}
										>
											<ChevronRight className="w-4 h-4" />
										</Button>
									</div>
									<p className="text-sm font-heading capitalize">
										{resultsRangeLabel}
									</p>
								</div>
								{agendaWeeks.map((weekItem, weekIndex) => (
									<div
										key={`${weekItem.weekStart.toISOString()}-${weekIndex}`}
										className="space-y-2"
									>
										<div className="flex items-center justify-between gap-3">
											<p className="text-xs uppercase tracking-widest text-muted-foreground">
												{t("week")} {weekIndex + 1}
											</p>
											<p className="text-xs text-muted-foreground">
												{weekItem.weekStart.toLocaleDateString(
													language === "it" ? "it-IT" : "en-US",
													{
														day: "numeric",
														month: "short",
													},
												)}
												-
												{weekItem.weekEnd.toLocaleDateString(
													language === "it" ? "it-IT" : "en-US",
													{
														day: "numeric",
														month: "short",
													},
												)}
											</p>
										</div>
										{weekItem.activeDays.length === 0 ? (
											<div className="text-xs text-muted-foreground border border-dashed border-border rounded-sm px-3 py-2">
												{t("noAgendaResults")}
											</div>
										) : (
											<div className="w-full overflow-x-auto pb-2 md:whitespace-nowrap">
												<div className="flex flex-col gap-3 md:flex-row md:flex-nowrap">
													{weekItem.activeDays.map((dayItem) => {
														const isToday =
															dayItem.dateKey === toDateKey(new Date());
														return (
															<div
																key={dayItem.dateKey}
																className={`border rounded-sm p-3 space-y-3 whitespace-normal w-full md:w-[320px] md:min-w-[320px] md:shrink-0 md:min-h-[280px] ${
																	isToday
																		? "border-volt/60 bg-volt/5"
																		: "border-border bg-card"
																}`}
															>
																<div className="border-b border-border pb-2">
																	<p className="font-heading text-sm capitalize">
																		{dayItem.label}
																	</p>
																	<p className="text-[11px] uppercase tracking-[0.18em] text-muted-foreground">
																		{dayItem.dayResults.length}{" "}
																		{dayItem.dayResults.length === 1
																			? t("resultLabel")
																			: t("resultsAnalysis")}
																	</p>
																	{!dayItem.hasLoggedResults && (
																		<div className="mt-1 inline-flex items-center rounded-sm border border-dashed border-border px-1.5 py-0.5 text-[11px] uppercase tracking-[0.18em] text-muted-foreground">
																			{t("noResultsLoadedShort")}
																		</div>
																	)}
																</div>
																<div className="space-y-3">
																	{dayItem.dayResults.map((result) =>
																		renderAgendaResultCard(result),
																	)}
																</div>
															</div>
														);
													})}
												</div>
											</div>
										)}
									</div>
								))}
							</div>
						)}
					</div>
				)}

				{/* ════════ INFO SECTION ════════ */}
				{activeSection === "info" && (
					<div className="space-y-4">
						<p className="label-tactical">{t("personalInfo")}</p>
						<Card className="bg-card border-border rounded-sm">
							<CardContent className="p-4 space-y-3">
								<div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
									<div>
										<p className="text-[10px] text-muted-foreground uppercase tracking-widest mb-0.5">
											{t("name")}
										</p>
										<p className="text-sm leading-snug break-words">
											{athlete.name}
										</p>
									</div>
									{athlete.nickname && (
										<div>
											<p className="text-[10px] text-muted-foreground uppercase tracking-widest mb-0.5">
												Nickname
											</p>
											<p className="text-sm leading-snug break-words">
												{athlete.nickname}
											</p>
										</div>
									)}
									<div>
										<p className="text-[10px] text-muted-foreground uppercase tracking-widest mb-0.5">
											Email
										</p>
										<p className="text-sm leading-snug break-all">
											{athlete.email}
										</p>
									</div>
									{athleteSportsOf(athlete).length > 0 && (
										<div>
											<p className="text-[10px] text-muted-foreground uppercase tracking-widest mb-0.5">
												{t("sport")}
											</p>
											<p className="text-sm leading-snug break-words">
												{athleteSportsOf(athlete)
													.map((sport) => formatSportLabel(sport, sportsOptions))
													.join(" · ")}
											</p>
										</div>
									)}
									{athlete.weight && (
										<div>
											<p className="text-[10px] text-muted-foreground uppercase tracking-widest mb-0.5">
												{t("weight")}
											</p>
											<p className="text-sm leading-snug break-words">
												{athlete.weight} kg
											</p>
										</div>
									)}
									{athlete.height && (
										<div>
											<p className="text-[10px] text-muted-foreground uppercase tracking-widest mb-0.5">
												{t("height")}
											</p>
											<p className="text-sm leading-snug break-words">
												{athlete.height} cm
											</p>
										</div>
									)}
									{athlete.date_of_birth && (
										<div>
											<p className="text-[10px] text-muted-foreground uppercase tracking-widest mb-0.5">
												{t("dateOfBirth")}
											</p>
											<p className="text-sm leading-snug break-words">
												{new Date(
													athlete.date_of_birth + "T12:00:00",
												).toLocaleDateString(
													language === "it" ? "it-IT" : "en-US",
													{ day: "numeric", month: "long", year: "numeric" },
												)}
											</p>
										</div>
									)}
								</div>
								{athlete.notes && (
									<div>
										<p className="text-[10px] text-muted-foreground uppercase tracking-widest mb-0.5">
											{t("notes")}
										</p>
										<p className="text-sm text-muted-foreground whitespace-pre-line">
											{athlete.notes}
										</p>
									</div>
								)}
							</CardContent>
						</Card>

						{/* Assigned Programs */}
						{programs.length > 0 && (
							<div>
								<p className="label-tactical mb-2">{t("assignedPrograms")}</p>
								<div className="space-y-1.5">
									{programs.map((prog) => (
										<Card
											key={prog.program_id}
											className="bg-card border-border rounded-sm cursor-pointer hover:border-volt/50 transition-colors"
											onClick={() => navigate(`/programs/${prog.program_id}`)}
										>
											<CardContent className="p-3 flex items-center justify-between gap-2">
												<div className="min-w-0">
													<p className="font-heading text-sm truncate">
														{prog.title}
													</p>
													{prog.created_at && (
														<p className="text-[10px] text-muted-foreground">
															{formatDate(prog.created_at)}
														</p>
													)}
												</div>
												<Badge className="rounded-sm bg-volt/20 text-volt border-0 text-[10px]">
													{getProgramDayCount(prog)} {t("days")}
												</Badge>
											</CardContent>
										</Card>
									))}
								</div>
							</div>
						)}
					</div>
				)}

				{/* ════════ PERFORMANCE PROFILE SECTION ════════ */}
				{activeSection === "profile" && (
					<div className="space-y-4">
						<p className="label-tactical">{t("performanceProfile")}</p>
						{/* Un tab per sport: prima il coach vedeva solo la primaria
						    (B4 di docs/sport-config/ANALYSIS.md). */}
						<CoachAthletePerformanceProfile
							athlete={athlete}
							sportsOptions={sportsOptions}
						/>
						{/* Armi e classe di merito del tiratore: stavano nella modale
						    della lista atleti, che ora apre questa pagina. Senza, il
						    coach non vede con che arma registra, da cui si deriva la
						    division. */}
						{hasDomain(user, DOMAIN_SHOOTING) && athlete?.user_id && (
							<div className="pt-4 border-t border-border/50">
								<ShootingProfileSection
									athleteId={athlete.user_id}
									coachMode
									shootingSports={[]}
									shootingClasses={athlete?.shooting_classes || {}}
								/>
							</div>
						)}
					</div>
				)}
			</div>

			<AgentChatSheet
				open={isAiOpen}
				onOpenChange={setIsAiOpen}
				title={`Analisi di ${athlete?.name || "atleta"}`}
				description="Chiedi un'analisi dei risultati, o un programma per questo atleta."
				athleteIds={athleteId ? [athleteId] : []}
			/>
		</Layout>
	);
}
